/**
 * Board coordinate mapping — a direct port of jungle/gui/board_view.py.
 *
 * The `flipped` transform is a pure display involution: it maps board
 * coordinates to display coordinates and back, and never touches game state,
 * sides, or turn.
 */

export const ROWS = 9;
export const COLS = 7;
export const MARGIN = 6;

export type Pos = [row: number, col: number];

export interface GridGeometry {
  cell: number;
  originX: number;
  originY: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Board position → displayed (row, col) after the flip transform. */
export function displayPos(pos: Pos, flipped: boolean): Pos {
  return flipped ? [ROWS - 1 - pos[0], COLS - 1 - pos[1]] : [pos[0], pos[1]];
}

/** Displayed (row, col) → board position (the transform is an involution). */
export function boardPos(dpos: Pos, flipped: boolean): Pos {
  return displayPos(dpos, flipped);
}

/** Cell size and origin centering the grid in a CSS-pixel box. */
export function gridGeometry(cssWidth: number, cssHeight: number): GridGeometry {
  const cell = Math.min((cssWidth - 2 * MARGIN) / COLS, (cssHeight - 2 * MARGIN) / ROWS);
  return {
    cell,
    originX: (cssWidth - cell * COLS) / 2,
    originY: (cssHeight - cell * ROWS) / 2,
  };
}

/** CSS-pixel rectangle of a BOARD position (flip-aware). */
export function cellRect(pos: Pos, flipped: boolean, geom: GridGeometry): Rect {
  const [drow, dcol] = displayPos(pos, flipped);
  return {
    x: geom.originX + dcol * geom.cell,
    y: geom.originY + drow * geom.cell,
    w: geom.cell,
    h: geom.cell,
  };
}

/** Center point of a board position in CSS pixels (flip-aware). */
export function cellCenter(pos: Pos, flipped: boolean, geom: GridGeometry): { x: number; y: number } {
  const rect = cellRect(pos, flipped, geom);
  return { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 };
}

/** BOARD position at CSS-pixel point (x, y), or null off the grid. */
export function posFromPoint(x: number, y: number, flipped: boolean, geom: GridGeometry): Pos | null {
  const dcol = Math.floor((x - geom.originX) / geom.cell);
  const drow = Math.floor((y - geom.originY) / geom.cell);
  if (drow >= 0 && drow < ROWS && dcol >= 0 && dcol < COLS) {
    return boardPos([drow, dcol], flipped);
  }
  return null;
}

export interface FrameGeometry {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Ring thickness in CSS px. */
  t: number;
  radius: number;
}

/**
 * Wooden frame ring around the grid: `t` thick, straddling the grid border
 * (up to half of it extends into the margin, the rest overlaps the outermost
 * cells and is painted beneath the pieces).
 */
export function frameGeometry(geom: GridGeometry): FrameGeometry {
  const t = Math.min(12, Math.max(8, geom.cell * 0.16));
  const out = Math.min(t / 2, geom.originX, geom.originY);
  return {
    x: geom.originX - out,
    y: geom.originY - out,
    w: geom.cell * COLS + 2 * out,
    h: geom.cell * ROWS + 2 * out,
    t,
    radius: t * 0.9,
  };
}
